/**
 * The two status axes (INV-02 "Header", SPEC-IVC LLD 3.1/3.2).
 *
 * `documentStatus` and `paymentStatus` are orthogonal: an invoice is `issued`
 * (document) while payment is `partially_paid`, and rendering one badge that
 * merges them is the `payment_axis_collapsed_into_document_axis` bug signal in
 * this page's spec. They are therefore two elements with their own accessible
 * names and their own test ids, and neither reads the other's value: each is
 * rendered by its own lookup call, not by a loop over a pair.
 *
 * The label and tone for each wire value come from `features/invoicing/status.ts`
 * — this component never maps a value itself, so an unrecognised value renders
 * the shared neutral "Unknown" rather than a raw enum member.
 */

import { Badge } from "@/components/ui/badge";
import type { Tone } from "@/components/ui/status";
import {
  documentStatusPresentation,
  paymentStatusPresentation,
  type StatusPresentation,
} from "@/features/invoicing/status";
import type { DocumentStatus, PaymentStatus } from "@/features/invoicing/types";
import { cn } from "@/lib/utils";

/** A status value rendered as a labelled chip; `title` keeps the wire value as evidence. */
export function StatusChip({
  presentation,
  raw,
  testId,
  className,
}: {
  presentation: StatusPresentation;
  /** The wire value, kept in the `title` so a reviewer can read what the service sent. */
  raw: string;
  testId?: string;
  className?: string;
}) {
  return (
    <span title={raw} data-testid={testId} className={cn("inline-flex", className)}>
      <Badge variant={presentation.tone as Tone}>{presentation.label}</Badge>
    </span>
  );
}

export interface StatusAxisLabels {
  heading: string;
  document: string;
  payment: string;
  note: string;
  paidLate: string;
}

export function StatusAxes({
  documentStatus,
  paymentStatus,
  paidLate,
  labels,
}: {
  documentStatus: DocumentStatus;
  paymentStatus: PaymentStatus;
  paidLate: boolean;
  labels: StatusAxisLabels;
}) {
  const axisLabel = "text-[11px] font-semibold uppercase tracking-[0.06em] text-muted";
  return (
    <section
      aria-label={labels.heading}
      className="mb-4 rounded-lg border border-line bg-surface px-4 py-3"
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2">
          <span className={axisLabel}>{labels.document}</span>
          <StatusChip
            presentation={documentStatusPresentation(documentStatus)}
            raw={String(documentStatus)}
            testId="invoice-document-status"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className={axisLabel}>{labels.payment}</span>
          <StatusChip
            presentation={paymentStatusPresentation(paymentStatus)}
            raw={String(paymentStatus)}
            testId="invoice-payment-status"
          />
        </div>
        {paidLate && (
          <span data-testid="invoice-paid-late" className="text-xs font-semibold text-warning">
            {labels.paidLate}
          </span>
        )}
      </div>
      <p className="mt-2 text-xs leading-relaxed text-muted">{labels.note}</p>
    </section>
  );
}
